import { makeStyles } from '@mui/styles';

export const useStyles = makeStyles((theme) => ({
  carouselContent: {
    width: 'calc(100% - 30px)',
    maxWidth: 1200,
    position: 'relative',
    margin: '0 auto',
    padding: '0 40px',
    opacity: 0,
    transform: 'translateY(20px)',
    transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
    [theme.breakpoints.down('sm')]: {
      padding: '0 20px',
    },
    '&.visible': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  closeButtonWrapper: {
    position: 'relative',
    width: 'calc(100% - 80px)',
    margin: '0 auto 5px',
    display: 'flex',
    justifyContent: 'flex-end',

    '& $closeButton': {
      color: '#f56100',
      padding: '0',
      marginBottom: '8px',
      '&:hover': {
        backgroundColor: 'rgba(245, 97, 0, 0.04)',
      },
      '& svg': {
        fontSize: 42,
      },
    },
  },
  '@keyframes rippleEffect': {
    '0%': {
      transform: 'scale(1)',
      opacity: 1,
      backgroundColor: 'rgba(245, 97, 0, 0.2)',
    },
    '50%': {
      transform: 'scale(1.3)',
      opacity: 0.5,
    },
    '100%': {
      transform: 'scale(1.5)',
      opacity: 0,
    },
  },
  slider: {
    width: 'calc(100% - 80px)',
    [theme.breakpoints.down('sm')]: {
      width: 'calc(100% - 100px)',
    },
    cursor: 'pointer',
    margin: '0 auto',
    backgroundColor: 'transparent',
    '& .slick-list': {
      marginBottom: '2rem',
      margin: '0 -10px',
    },
    '@media (pointer: fine)': {
      '& .slick-track': {
        display: 'flex',
        margin: '0 auto',
        justifyContent: 'center',
        transition: 'transform 1.5s cubic-bezier(0.4, 0, 0.2, 1) !important',
        willChange: 'transform',
        transform: 'translate3d(0,0,0)',
        '-webkit-transform': 'translate3d(0,0,0)',
        '-webkit-backface-visibility': 'hidden',
        '-webkit-perspective': 1000,
        backfaceVisibility: 'hidden',
      },
      '&.auto-scrolling .slick-track': {
        transition: 'transform 3s cubic-bezier(0.1, 0, 0.1, 1) !important',
      },
      '& .slick-slide': {
        height: 'max-content',
        margin: '0 10px',
        transition: 'all 1.5s cubic-bezier(0.4, 0, 0.2, 1) !important',
        transform: 'translate3d(0,0,0)',
        '-webkit-transform': 'translate3d(0,0,0)',
        '-webkit-backface-visibility': 'hidden',
        '-webkit-perspective': 1000,
        backfaceVisibility: 'hidden',
        '& > div': {
          height: '100%',
        },
      },
    },
    '@media (pointer: coarse)': {
      '& .slick-track': {
        display: 'flex',
        margin: '0 auto',
        justifyContent: 'center',
        transition: 'transform 0.5s ease',
      },
      '&.auto-scrolling .slick-track': {
        transition: 'transform 0.8s ease',
      },
      '& .slick-slide': {
        height: 'auto',
        margin: '0 10px',
        transition: 'opacity 0.3s ease',
        '& > div': {
          height: '100%',
        },
      },
    },
    '& .slick-slide:first-child': {
      paddingLeft: 0,
    },
    '& .slick-slide:last-child': {
      paddingRight: 0,
    },
    '& .slick-arrow': {
      width: 50,
      height: 50,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1,
      cursor: 'pointer',
      '&::before': {
        display: 'none !important',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: '50%',
        pointerEvents: 'none',
      },
      '&:active::after': {
        '@media (pointer: fine)': {
          animation: '$rippleEffect 0.3s ease-out',
        },
      },
      '&.slick-disabled': {
        opacity: 0.3,
        cursor: 'not-allowed',
        '&:active::after': {
          animation: 'none',
        },
      },
      '& svg': {
        color: '#f56100',
        fontSize: 64,
      },
    },
    '& .slick-prev': {
      left: -60,
    },
    '& .slick-next': {
      right: -60,
    },
  },
  podcastCard: {
    boxSizing: 'border-box',
    position: 'relative',
    backgroundColor: 'var(--background-color-full)',
    borderRadius: 8,
    display: 'flex !important',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
    transition: 'transform 0.3s ease-in-out',
    cursor: 'grab',
    outline: 'none',
    padding: 10,
    maxWidth: 210,
    minHeight: 380,
    [theme.breakpoints.down('sm')]: {
      maxWidth: 310,
    },
    margin: '0 auto',
    '& $podcastTitle': {
      color: 'var(--text-color)',
      fontSize: '1rem',
      fontWeight: 'bold',
      textAlign: 'center',
      marginBottom: '0.5rem',
      display: '-webkit-box',
      '-webkit-line-clamp': 2,
      '-webkit-box-orient': 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxHeight: '3rem',
      minHeight: 48,
      lineHeight: '1.5rem',
      padding: '0 5px',
    },
    '& $podcastArtist': {
      fontSize: 14,
      fontWeight: 600,
      color: 'var(--text-secondary-color)',
      marginBottom: 5,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '100%',
    },
    '& $podcastRank': {
      fontSize: 52,
      fontWeight: 600,
      color: '#f56100',
    },
    '& $podcastGenre': {
      fontSize: 16,
      fontWeight: 400,
      color: 'var(--podcast-genre-color)',
      fontStyle: 'italic',
    },
    '& $podcastCountry': {
      fontSize: 14,
      fontWeight: 400,
      color: 'var(--text-color)',
      marginTop: 5,
    },
    '& $historyRank': {
      boxSizing: 'border-box',
      color: 'var(--text-color)',
      borderRadius: '50%',
      backgroundColor: 'var(--background-color-secondary)',
      width: 65,
      height: 65,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transform: 'scale(0.8) translateX(12px)',
      '& svg': {
        marginLeft: -5,
        marginBottom: 2,
        fontSize: 22,
      },
      '& $historyRankUp': {
        display: 'flex',
        alignItems: 'center',
        fontSize: 26,
        fontWeight: 600,
        color: '#9df500',
        '& svg': {
          fill: '#9df500',
        },
      },
      '& $historyRankDown': {
        display: 'flex',
        alignItems: 'center',
        fontSize: 26,
        fontWeight: 600,
        color: '#f50000',
      },
    },
  },
  podcastImage: {
    width: '100%',
    height: 188,
    borderRadius: 10,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: 'auto',
    },
  },
  podcastIcon: {
    width: 20,
    height: 20,
    marginRight: 5,
    '& path': {
      fill: 'var(--text-color)',
    },
  },
  rankContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
    padding: '5px 5px 0',
    boxSizing: 'border-box',
    alignItems: 'center',
    marginTop: 'auto',
  },
  noPodcastsText: {
    color: 'white',
    textAlign: 'center',
    fontSize: '1.2rem',
    marginBottom: '2rem !important',
  },
  podcastImageWrapper: {
    position: 'relative',
    width: '100%',
    marginBottom: 10,
    '&:hover $imageOverlay': {
      opacity: 1,
    },
  },
  imageOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    opacity: 0,
    transition: 'opacity 0.5s ease',
    borderRadius: 10,
    '& $overlayButton': {
      color: 'white ',
      fontSize: 14,
      padding: '5px 10px ',
      '&:hover': {
        color: '#46c0ec',
        backgroundColor: 'transparent',
      },
    },
  },
  lightThemeBorder: {
    border: '1px solid var(--border-color)',
  },
  lightThemeBoxShadow: {
    boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.4)',
  },
  fallbackContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--background-color-secondary)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    color: 'var(--text-color)',
  },
  fallbackText: {
    fontSize: '2rem',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  overlayButton: {},
  podcastRank: {},
  podcastArtist: {},
  podcastTitle: {},
  podcastGenre: {},
  historyRank: {},
  historyRankUp: {},
  historyRankDown: {},
  podcastCountry: {},
  closeButton: {},
}));
